import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { api, type CompareRequest } from '../lib/api.ts';
import { useFilterStore } from '../stores/filter-store.ts';
import { DateRangePicker } from '../components/DateRangePicker.tsx';
import { fmtCurrency, fmtNumber, fmtPercent } from '../lib/format.ts';
import { Icon } from '../components/Icon.tsx';
import { TableSkeleton } from '../components/Skeleton.tsx';

type GroupBy = CompareRequest['groupBy'];

const GROUP_BY_LABELS: Record<GroupBy, string> = {
  store_name: 'Filiale',
  warengruppe_name: 'Warengruppe',
  plz: 'PLZ-Region',
  monat: 'Monat',
  wochentag: 'Wochentag',
};

function todayMinus(days: number): string {
  const d = new Date();
  d.setDate(d.getDate() - days);
  return d.toISOString().substring(0, 10);
}

export function PeriodenvergleichPage() {
  const filters = useFilterStore((s) => s.filters);
  const [periodeA, setPeriodeA] = useState<{ min: string; max: string }>({
    min: todayMinus(30),
    max: todayMinus(1),
  });
  const [periodeB, setPeriodeB] = useState<{ min: string; max: string }>({
    min: todayMinus(60),
    max: todayMinus(31),
  });
  const [groupBy, setGroupBy] = useState<GroupBy>('store_name');

  const { data, isLoading } = useQuery({
    queryKey: ['compare', periodeA, periodeB, groupBy, filters],
    queryFn: () =>
      api.compare({
        filters,
        periode_a: periodeA,
        periode_b: periodeB,
        groupBy,
        limit: 100,
      }),
    enabled: !!periodeA.min && !!periodeA.max && !!periodeB.min && !!periodeB.max,
  });

  function swapPeriods() {
    setPeriodeA(periodeB);
    setPeriodeB(periodeA);
  }

  return (
    <div className="space-y-4 lg:space-y-6 w-full">
      <div>
        <div className="section-head mb-1.5">
          <span className="section-eyebrow">Bericht</span>
          <div className="section-rule" />
        </div>
        <h2 className="text-xl font-bold tracking-tight text-slate-900 dark:text-slate-100">
          Periodenvergleich
        </h2>
        <p className="text-sm text-slate-500 dark:text-slate-400 mt-1">
          Zwei beliebige Zeiträume parallel — mit Delta absolut und prozentual pro Gruppe.
        </p>
      </div>

      {/* Filter-Bar */}
      <div className="card card-body">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-3 items-end">
          <DateRangePicker
            label="Periode A (Aktuell)"
            value={periodeA}
            onChange={(v) => v.min && v.max && setPeriodeA({ min: v.min, max: v.max })}
          />
          <DateRangePicker
            label="Periode B (Vergleich)"
            value={periodeB}
            onChange={(v) => v.min && v.max && setPeriodeB({ min: v.min, max: v.max })}
          />
          <div>
            <label className="block text-2xs uppercase tracking-wider font-medium text-slate-500 dark:text-slate-400 mb-1">
              Gruppieren nach
            </label>
            <select
              value={groupBy}
              onChange={(e) => setGroupBy(e.target.value as GroupBy)}
              className="input"
            >
              {Object.entries(GROUP_BY_LABELS).map(([v, l]) => (
                <option key={v} value={v}>
                  {l}
                </option>
              ))}
            </select>
          </div>
          <button onClick={swapPeriods} className="btn-secondary">
            <Icon name="refresh" size={14} />
            Perioden tauschen
          </button>
        </div>
      </div>

      {/* Total-Karte */}
      {data && (
        <div className="grid grid-cols-1 md:grid-cols-4 gap-3 animate-fade-in">
          <div className="kpi-tile">
            <div className="kpi-label">Periode A</div>
            <div className="kpi-value">{fmtCurrency(data.total.umsatz_a)}</div>
            <div className="text-2xs text-slate-500 mt-1">
              {periodeA.min} – {periodeA.max}
            </div>
          </div>
          <div className="kpi-tile">
            <div className="kpi-label">Periode B</div>
            <div className="kpi-value">{fmtCurrency(data.total.umsatz_b)}</div>
            <div className="text-2xs text-slate-500 mt-1">
              {periodeB.min} – {periodeB.max}
            </div>
          </div>
          <div className="kpi-tile">
            <div className="kpi-label">Differenz absolut</div>
            <div
              className={`kpi-value ${
                data.total.diff_abs >= 0 ? 'kpi-delta-up' : 'kpi-delta-down'
              }`}
            >
              {data.total.diff_abs >= 0 ? '+' : ''}
              {fmtCurrency(data.total.diff_abs)}
            </div>
          </div>
          <div className="kpi-tile">
            <div className="kpi-label">Differenz %</div>
            <div
              className={`kpi-value flex items-center gap-2 ${
                (data.total.diff_pct ?? 0) >= 0 ? 'kpi-delta-up' : 'kpi-delta-down'
              }`}
            >
              <Icon
                name={(data.total.diff_pct ?? 0) >= 0 ? 'trendingUp' : 'trendingDown'}
                size={20}
              />
              {data.total.diff_pct !== null
                ? `${data.total.diff_pct >= 0 ? '+' : ''}${fmtPercent(data.total.diff_pct)}`
                : '—'}
            </div>
          </div>
        </div>
      )}

      {/* Tabelle */}
      <div className="card">
        <div className="card-header">
          <div>
            <h3 className="card-title">Detail-Vergleich nach {GROUP_BY_LABELS[groupBy]}</h3>
            <p className="card-subtitle">
              Top {data?.rows.length ?? 0} sortiert nach Umsatz in Periode A
            </p>
          </div>
        </div>
        <div className="card-body">
          {isLoading ? (
            <TableSkeleton rows={10} />
          ) : !data ? null : (
            <div className="table-scroll">
              <table className="data-table">
                <thead>
                  <tr>
                    <th>{GROUP_BY_LABELS[groupBy]}</th>
                    <th className="num">Umsatz A</th>
                    <th className="num">Umsatz B</th>
                    <th className="num">Δ Absolut</th>
                    <th className="num">Δ Prozent</th>
                    <th className="num">Bons A</th>
                    <th className="num">Bons B</th>
                  </tr>
                </thead>
                <tbody>
                  {data.rows.map((r) => {
                    const positive = r.diff_abs >= 0;
                    return (
                      <tr key={r.label}>
                        <td className="font-medium truncate max-w-xs">{r.label}</td>
                        <td className="num">{fmtCurrency(r.umsatz_a)}</td>
                        <td className="num text-slate-500">{fmtCurrency(r.umsatz_b)}</td>
                        <td
                          className={`num font-semibold ${
                            positive ? 'kpi-delta-up' : 'kpi-delta-down'
                          }`}
                        >
                          {positive ? '+' : ''}
                          {fmtCurrency(r.diff_abs)}
                        </td>
                        <td
                          className={`num font-semibold ${
                            positive ? 'kpi-delta-up' : 'kpi-delta-down'
                          }`}
                        >
                          {r.diff_pct !== null
                            ? `${positive ? '+' : ''}${fmtPercent(r.diff_pct)}`
                            : '—'}
                        </td>
                        <td className="num text-slate-500">{fmtNumber(r.anz_bons_a)}</td>
                        <td className="num text-slate-500">{fmtNumber(r.anz_bons_b)}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
